<template>
  <article class="logos-row-container">
    <!-- Default logo -->
    <section
      class="default-logo-container"
      v-if="defaultLogo && defaultLogo.id"
    >
      <div class="sidebar-option-title">Default</div>
      <div class="logo-row is-default">
        <SidebarLogoPreview :logo="defaultLogo" />
        <div class="logo-actions-container">
          <div class="edit-remove-row">
            <div class="edit-remove-options">
              <button
                :disabled="loading"
                title="edit logo"
                class="btn"
                @click="!loading && handleEditLogo(defaultLogo)"
              >
                <img
                  src="@/assets/icons/ui/pencil-outline-dark.svg"
                  alt="edit logo"
                />
              </button>
              <DeleteButton
                @click.native="
                  !loading && handleRemoveDefaultLogo(defaultLogo.id)
                "
              />
            </div>
          </div>
          <div class="text-container">
            <p>This is your primary graphic</p>
            <p>
              To save you time, we will decorate all items with this unless
              edited individually.
            </p>
          </div>
          <p class="logo-filename">{{ defaultLogo.filename }}</p>
        </div>
      </div>
    </section>

    <!-- Other Logos -->
    <section
      class="other-logos-container"
      v-if="otherLogos && otherLogos.length"
    >
      <div class="sidebar-option-title">Other graphics</div>
      <div
        class="logo-row"
        v-for="logo in otherLogos"
        :key="'sidebar-logo-row-' + logo.id"
      >
        <SidebarLogoPreview :logo="logo" />
        <div class="logo-actions-container">
          <div class="edit-remove-row">
            <p class="logo-filename">{{ logo.filename }}</p>
            <div class="edit-remove-options">
              <button
                :disabled="loading"
                title="edit logo"
                class="btn"
                @click="!loading && handleEditLogo(logo)"
              >
                <img
                  src="@/assets/icons/ui/pencil-outline-dark.svg"
                  alt="edit logo"
                />
              </button>
              <DeleteButton
                @click.native="!loading && handleDeleteLogo(logo.id)"
              />
            </div>
          </div>
          <button
            class="btn btn-outline"
            @click="handleSetDefaultLogo(logo.id)"
          >
            Make default graphic
          </button>
        </div>
      </div>
    </section>
    <UploadLogoButton
      :refName="'sidebar-add-logo'"
      :graphicButtonHint="'Add Graphic'"
      :type="'upload'"
      :uploadToThreekit="true"
    />
    <transition name="fade">
      <div class="loader-container" v-if="loading">
        <Loader />
      </div>
    </transition>
  </article>
</template>

<script>
import SidebarLogoPreview from "@/components/Projects/Project/Sidebar/SidebarLogoPreview.vue";
import UploadLogoButton from "@/components/Utils/Logos/UploadLogoButton.vue";
import DeleteButton from "@/components/Utils/DeleteButton.vue";
import Loader from "@/components/Utils/Loader.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { cloneDeep } from "lodash";
export default {
  name: "SidebarLogos",
  components: {
    DeleteButton,
    Loader,
    SidebarLogoPreview,
    UploadLogoButton,
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState("project", ["projectLogos", "defaultLogoId"]),

    ...mapGetters("project", ["projectColors", "defaultLogo"]),

    otherLogos() {
      return this.projectLogos?.filter(
        (logo) => logo?.id !== this.defaultLogoId
      );
    },
  },

  methods: {
    ...mapActions("project", [
      "deleteProjectLogo",
      "setNewDefautlLogo",
      "setDefaultLogo",
      "removeDefaultLogo",
      "updateDecorationsForProjectProductsLayouts"
    ]),

    ...mapActions("editableLogo", ["DIGIrequestImageAsBse64", "setApiLoading"]),
    ...mapActions("uploadLogos", ["openEditLogoModalToEditSingleLogo"]),

    async handleSetDefaultLogo(logoId) {
      try {
        this.loading = true;
        await this.setNewDefautlLogo(logoId);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async handleRemoveDefaultLogo(logoId) {
      try {
        this.loading = true;
        await this.removeDefaultLogo(logoId);
        await this.deleteProjectLogo(logoId);
        if (this.otherLogos.length) {
          await this.handleSetDefaultLogo(this.otherLogos[0].id);
        } else {
          //Update 2D decorations to disappear the deleted logo
          this.updateDecorationsForProjectProductsLayouts();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async handleDeleteLogo(logoId) {
      try {
        this.loading = true;
        await this.deleteProjectLogo(logoId);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async handleEditLogo(logo) {
      try {
        this.setApiLoading(true);
        if (logo.content) {
          await this.openEditLogoModalToEditSingleLogo(logo);
        } else {
          //Convert the url to a base64 string
          this.openEditLogoModalToEditSingleLogo();
          const base64 = await this.DIGIrequestImageAsBse64(logo.url);
          const logoClone = cloneDeep({ ...logo, content: base64 });
          await this.openEditLogoModalToEditSingleLogo(logoClone);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setApiLoading(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.logos-row-container {
  position: relative;

  .other-logos-container {
    margin-bottom: 1.5rem;
  }

  .logo-row {
    border-radius: 6px;
    display: flex;
    margin-bottom: 1rem;
    transition: all 0.2s;
    border: 1px solid transparent;
    height: 80px;
    &.is-default {
      background-color: white;
    }

    &:not(.is-default) {
      .edit-remove-options {
        opacity: 0;
        pointer-events: none;
      }

      ::v-deep .btn:hover {
        background: white !important;
      }

      &:hover {
        border: 1px solid $grey-dark;
        .edit-remove-row {
          .edit-remove-options {
            opacity: 1 !important;
            pointer-events: initial !important;
          }
        }

        .btn-outline {
          opacity: 1 !important;
          pointer-events: initial !important;
        }
      }

      .btn-outline {
        /* opacity: 0; */
        /* pointer-events: none; */
      }
    }

    ::v-deep .logo-box {
      margin-right: 1rem;
      height: 80px;
      min-width: 80px;
      min-width: 80px !important;
    }

    .logo-actions-container {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      padding-right: 0.5rem;
      width: calc(100% - 80px - 1rem);
      position: relative;
      .edit-remove-row {
        display: flex;
        justify-content: space-between;
        margin-top: 0.5rem;
        position: absolute;
        right: 0.5em;
        top: 0;
        width: 100%;

        .edit-remove-options {
          display: flex;
          margin-left: auto;
          /* opacity: 0; */
          /* pointer-events: none; */

          button:first-child {
            margin-right: 0.8rem;
          }

          button.btn,
          button {
            background: none;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 2rem;
            width: 2rem;
            padding: 0.1rem !important;

            &:hover {
              /* background: white; */
            }
          }
        }
      }

      .text-container {
        /* max-width: 75%; */
        padding: 0.75rem;
        p {
          color: $grey-dark;
          font-size: 0.9rem;
          &:first-child {
            margin-bottom: 0.5rem;
          }
        }
      }

      .btn-outline {
        border: 1px solid $grey-dark !important;
        color: $grey-dark;
        padding: 0.5rem;
        width: fit-content;
        text-transform: capitalize;
        margin-bottom: 0.5rem;
        margin-top: auto;
      }

      .logo-filename {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        max-width: 50%;
        font-size: 0.85em;
      }
    }
  }

  .loader-container {
    position: absolute;
    top: 0%;
    left: 0%;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.5);
    display: flex;
  }
}
</style>
